'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Copy, CheckCircle, ArrowRight, GraduationCap, Users, Shield, UserCheck, BookOpen } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';


type Role = 'super-admin' | 'l1-executive' | 'l2-executive' | 'l3-executive' | 'instructor';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

const demoCredentials: Array<{ role: Role; label: string; email: string; password: string; icon: React.ReactNode; color: string; redirect: string }> = [
  {
    role: 'super-admin',
    label: 'Super Admin',
    email: 'rajan.deb@tripuraedu.in',
    password: 'Admin@TEH2026',
    icon: <Shield size={18} />,
    color: 'hierarchy-admin',
    redirect: '/super-admin-dashboard',
  },
  {
    role: 'l1-executive',
    label: 'L1 Executive',
    email: 'suresh.chakraborty@tripuraedu.in',
    password: 'L1Exec@TEH26',
    icon: <UserCheck size={18} />,
    color: 'hierarchy-l1',
    redirect: '/executive-dashboard',
  },
  {
    role: 'l2-executive',
    label: 'L2 Executive',
    email: 'priya.sharma@tripuraedu.in',
    password: 'L2Exec@TEH26',
    icon: <Users size={18} />,
    color: 'hierarchy-l2',
    redirect: '/executive-dashboard',
  },
  {
    role: 'l3-executive',
    label: 'L3 Executive',
    email: 'ananya.das@tripuraedu.in',
    password: 'L3Exec@TEH26',
    icon: <GraduationCap size={18} />,
    color: 'hierarchy-l3',
    redirect: '/student-enrollment-management',
  },
  {
    role: 'instructor',
    label: 'Instructor',
    email: 'dr.subir.datta@tripuraedu.in',
    password: 'Instr@TEH26',
    icon: <BookOpen size={18} />,
    color: 'bg-emerald-600 text-white',
    redirect: '/instructor-dashboard',
  },
];

const roleDescriptions: Record<Role, string> = {
  'super-admin': 'Full platform control — manage executives, students, courses, and all transactions.',
  'l1-executive': 'Create courses, add students, collect fees, manage L2 executives under you.',
  'l2-executive': 'Enroll students in available courses. 10% of your collected fees go to your L1.',
  'l3-executive': 'Enroll students in available courses. 20% of fees go up your hierarchy chain.',
  'instructor': 'Manage your assigned courses, deliver content, track student progress and assessments.',
};

export default function LoginPageClient() {
  const [selectedRole, setSelectedRole] = useState<Role>('super-admin');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/login with { email, password, role: selectedRole }
    await new Promise((r) => setTimeout(r, 1200));

    const match = demoCredentials.find(
      (c) => c.email === data.email && c.password === data.password && c.role === selectedRole
    );

    if (!match) {
      setIsLoading(false);
      setError('email', {
        type: 'manual',
        message: 'Invalid credentials — use the demo accounts below to sign in.',
      });
      return;
    }

    toast.success(`Welcome back! Redirecting to ${match.label} dashboard...`);
    setIsLoading(false);
    window.location.href = match.redirect;
  };

  const handleAutofill = (cred: typeof demoCredentials[0]) => {
    setSelectedRole(cred.role);
    setValue('email', cred.email);
    setValue('password', cred.password);
    toast.info(`Credentials filled for ${cred.label}`);
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success('Copied to clipboard');
  };

  const selectedCred = demoCredentials.find((c) => c.role === selectedRole)!;

  return (
    <div className="min-h-screen flex">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-primary flex-col justify-between p-10 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-accent/20 translate-y-1/2 -translate-x-1/2" />
        <div className="absolute top-1/2 left-1/2 w-80 h-80 rounded-full bg-white/3 -translate-x-1/2 -translate-y-1/2" />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <AppLogo size={44} />
          <div>
            <span className="font-bold text-white text-xl leading-tight block">Shivala Shiksha Sansthaan</span>
            <span className="text-white/60 text-sm">Multi-Level Education Platform</span>
          </div>
        </div>

        {/* Hero content */}
        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white leading-tight mb-4">
            Admissions, Commissions &<br />
            <span className="text-accent">Learning — All in One.</span>
          </h1>
          <p className="text-white/70 text-base leading-relaxed max-w-md mb-8">
            Manage multi-level executive networks, automate fee commission distribution, and deliver quality education through an integrated LMS — purpose-built for Tripura.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { value: '2,400+', label: 'Students Enrolled' },
              { value: '₹18.4L', label: 'Fees Collected' },
              { value: '36', label: 'Active Courses' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <p className="text-2xl font-bold text-white tabular-nums">{stat.value}</p>
                <p className="text-xs text-white/60 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Level hierarchy visual */}
        <div className="relative z-10">
          <p className="text-xs text-white/40 uppercase tracking-widest mb-3 font-medium">Executive Hierarchy</p>
          <div className="flex items-center gap-2">
            {[
              { label: 'L1 Executive', color: 'bg-blue-400' },
              { label: '→', color: '' },
              { label: 'L2 Executive', color: 'bg-amber-400' },
              { label: '→', color: '' },
              { label: 'L3 Executive', color: 'bg-purple-400' },
              { label: '→', color: '' },
              { label: 'Students', color: 'bg-green-400' },
            ].map((item, i) => (
              item.label === '→'
                ? <span key={`arrow-${i}`} className="text-white/40 text-lg">→</span>
                : (
                  <span key={`level-${item.label}`} className={`${item.color} text-white text-xs font-semibold px-2.5 py-1 rounded-full`}>
                    {item.label}
                  </span>
                )
            ))}
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="w-full lg:w-1/2 xl:w-[45%] flex items-center justify-center p-6 lg:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-3 mb-8">
            <AppLogo size={36} />
            <span className="font-bold text-primary text-lg">Shivala Shiksha Sansthaan</span>
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-1">Sign in to your portal</h2>
          <p className="text-sm text-muted-foreground mb-6">Select your role and enter your credentials to continue.</p>

          {/* Role selector */}
          <div className="mb-5">
            <p className="text-xs font-600 uppercase tracking-wider text-muted-foreground mb-2">Select Your Role</p>
            <div className="grid grid-cols-2 gap-2">
              {demoCredentials.map((cred) => (
                <button
                  key={`role-${cred.role}`}
                  onClick={() => setSelectedRole(cred.role)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-left transition-all ${
                    selectedRole === cred.role
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-muted/50'
                  }`}
                >
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${cred.color}`}>
                    {cred.icon}
                  </span>
                  <span className={`text-sm font-semibold ${selectedRole === cred.role ? 'text-primary' : 'text-foreground'}`}>
                    {cred.label}
                  </span>
                </button>
              ))}
            </div>
            {selectedRole && (
              <p className="text-xs text-muted-foreground mt-2 px-1">{roleDescriptions[selectedRole]}</p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-1">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="your.name@tripuraedu.in"
                className={`w-full px-3 py-2.5 rounded-xl border text-sm bg-card text-foreground placeholder:text-muted-foreground outline-none transition-colors ${
                  errors.email ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                }`}
                {...register('email', {
                  required: 'Email address is required.',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
                })}
              />
              {errors.email && (
                <p className="text-xs text-danger mt-1 font-medium">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-foreground mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`w-full px-3 py-2.5 pr-10 rounded-xl border text-sm bg-card text-foreground placeholder:text-muted-foreground outline-none transition-colors ${
                    errors.password ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                  }`}
                  {...register('password', { required: 'Password is required.' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-danger mt-1 font-medium">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-border accent-primary"
                  {...register('remember')}
                />
                <span className="text-sm text-foreground">Remember me</span>
              </label>
              <button type="button" className="text-sm text-primary font-medium hover:underline">
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In to {selectedCred.label}</span>
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6 border border-border rounded-xl overflow-hidden">
            <div className="bg-muted px-4 py-2.5 border-b border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Demo Credentials — Click to Autofill</p>
            </div>
            <div className="divide-y divide-border">
              {demoCredentials.map((cred) => (
                <div key={`demo-${cred.role}`} className="px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors">
                  <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs ${cred.color} flex-shrink-0`}>
                    {cred.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground">{cred.label}</p>
                    <p className="text-xs text-muted-foreground truncate">{cred.email}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleCopy(cred.email, `email-${cred.role}`)}
                      className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      aria-label={`Copy email for ${cred.label}`}
                    >
                      {copiedField === `email-${cred.role}` ? <CheckCircle size={13} className="text-success" /> : <Copy size={13} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAutofill(cred)}
                      className="text-xs btn-accent px-2 py-1 rounded-lg font-semibold"
                    >
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-4">
            By signing in, you agree to the{' '}
            <span className="text-primary font-medium cursor-pointer hover:underline">Terms of Service</span>
            {' '}and{' '}
            <span className="text-primary font-medium cursor-pointer hover:underline">Privacy Policy</span>
            {' '}of Shivala Shiksha Sansthaan.
          </p>
        </div>
      </div>
    </div>
  );
}